import { validatePhoneNumber, validateEmail } from './phoneFormatter';

export interface ValidationResult {
  isValid: boolean;
  error: string;
}

export const validateAdSoyad = (value: string): ValidationResult => {
  const trimmed = value.trim();
  
  if (!trimmed) {
    return {
      isValid: false,
      error: 'Ad ve soyad alanı zorunludur. Lütfen adınızı ve soyadınızı giriniz.'
    };
  }
  
  if (trimmed.length < 2) {
    return {
      isValid: false,
      error: 'Ad ve soyad en az 2 karakter olmalıdır.'
    };
  }
  
  if (trimmed.length > 100) {
    return {
      isValid: false,
      error: 'Ad ve soyad en fazla 100 karakter olabilir.'
    };
  }
  
  const nameRegex = /^[a-zA-ZçğıöşüÇĞIİÖŞÜ\s]+$/;
  if (!nameRegex.test(trimmed)) {
    return {
      isValid: false,
      error: 'Ad ve soyad sadece harf içerebilir.'
    };
  }
  
  return { isValid: true, error: '' };
};

const VALID_TURKISH_MOBILE_PREFIXES = [
  '500', '501', '502', '503', '504', '505', '506', '507', '508', '509',
  '530', '531', '532', '533', '534', '535', '536', '537', '538', '539',
  '540', '541', '542', '543', '544', '545', '546', '547', '548'
];

const isValidTurkishMobilePrefix = (phoneNumber: string): boolean => {
  const numbers = phoneNumber.replace(/\D/g, '');
  
  let cleanNumbers = numbers;
  if (numbers.startsWith('90')) {
    cleanNumbers = numbers.substring(2);
  } else if (numbers.startsWith('0')) {
    cleanNumbers = numbers.substring(1);
  }
  
  if (cleanNumbers.length < 10) {
    return false;
  }
  
  const prefix = cleanNumbers.substring(0, 3);
  return VALID_TURKISH_MOBILE_PREFIXES.includes(prefix);
};

export const validateTelefon = (value: string, isRequired: boolean = true): ValidationResult => {
  const trimmed = value.trim();
  
  if (!trimmed || trimmed === '+90') {
    if (isRequired) {
      return {
        isValid: false,
        error: 'Telefon numarası alanı zorunludur. Lütfen geçerli bir telefon numarası giriniz.'
      };
    }
    return { isValid: true, error: '' };
  }
  
  if (!validatePhoneNumber(trimmed)) {
    const numbers = trimmed.replace(/\D/g, '');
    if (numbers.length < 10) {
      return {
        isValid: false,
        error: 'Telefon numarası en az 10 haneli olmalıdır. Örnek: +90 555 123 45 67'
      };
    }
    if (numbers.length > 12) {
      return {
        isValid: false,
        error: 'Telefon numarası en fazla 12 haneli olabilir.'
      };
    }
    return {
      isValid: false,
      error: 'Geçersiz telefon numarası formatı. Örnek: +90 555 123 45 67'
    };
  }
  
  if (!isValidTurkishMobilePrefix(trimmed)) {
    return {
      isValid: false,
      error: 'Geçerli bir telefon numarası giriniz.'
    };
  }
  
  return { isValid: true, error: '' };
};

export const validateEmailField = (value: string, isRequired: boolean = true): ValidationResult => {
  const trimmed = value.trim();
  
  if (!trimmed) {
    if (isRequired) {
      return {
        isValid: false,
        error: 'E-posta adresi alanı zorunludur. Lütfen geçerli bir e-posta adresi giriniz.'
      };
    }
    return { isValid: true, error: '' };
  }
  
  if (!validateEmail(trimmed)) {
    return {
      isValid: false,
      error: 'Geçersiz e-posta formatı. Örnek: ornek@email.com'
    };
  }
  
  if (trimmed.length > 254) {
    return {
      isValid: false,
      error: 'E-posta adresi çok uzun. Maksimum 254 karakter olabilir.'
    };
  }
  
  return { isValid: true, error: '' };
};

export const validateSehir = (value: string): ValidationResult => {
  if (!value || !value.trim()) {
    return {
      isValid: false,
      error: 'Şehir seçimi zorunludur. Lütfen bir şehir seçiniz.'
    };
  }
  
  return { isValid: true, error: '' };
};

export const validateAdres = (value: string): ValidationResult => {
  const trimmed = value.trim();
  
  if (!trimmed) {
    return {
      isValid: false,
      error: 'Adres alanı zorunludur. Lütfen adresinizi giriniz.'
    };
  }
  
  if (trimmed.length < 10) {
    return {
      isValid: false,
      error: 'Adres en az 10 karakter olmalıdır. Lütfen daha detaylı bir adres giriniz.'
    };
  }
  
  if (trimmed.length > 500) {
    return {
      isValid: false,
      error: 'Adres en fazla 500 karakter olabilir.'
    };
  }
  
  return { isValid: true, error: '' };
};

export const validateMesaj = (value: string, isRequired: boolean = false): ValidationResult => {
  const trimmed = value.trim();
  
  if (!trimmed && isRequired) {
    return {
      isValid: false,
      error: 'Bu alan zorunludur. Lütfen bir mesaj giriniz.'
    };
  }
  
  if (trimmed.length > 1000) {
    return {
      isValid: false,
      error: 'Mesaj en fazla 1000 karakter olabilir.'
    };
  }
  
  return { isValid: true, error: '' };
};

export const validateWhatsAppOrTelefon = (
  whatsapp: string, 
  telefon: string
): ValidationResult => {
  const whatsappTrimmed = whatsapp.trim();
  const telefonTrimmed = telefon.trim();
  const telefonNumbers = telefonTrimmed.replace(/\D/g, '');
  
  if ((!whatsappTrimmed || whatsappTrimmed === '+90') && 
      (!telefonTrimmed || telefonTrimmed === '+90' || telefonNumbers.length < 10)) {
    return {
      isValid: false,
      error: 'WhatsApp veya telefon numarası alanlarından en az biri doldurulmalıdır. Lütfen geçerli bir numara giriniz.'
    };
  }
  
  if (whatsappTrimmed && whatsappTrimmed !== '+90') {
    const whatsappValidation = validateTelefon(whatsappTrimmed, false);
    if (!whatsappValidation.isValid) {
      return whatsappValidation;
    }
  }
  
  if (telefonTrimmed && telefonTrimmed !== '+90' && telefonNumbers.length >= 10) {
    const telefonValidation = validateTelefon(telefonTrimmed, false);
    if (!telefonValidation.isValid) {
      return telefonValidation;
    }
  }
  
  return { isValid: true, error: '' };
};

